import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Lightbulb } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import { AAI_DIMENSIONS, DIMENSION_LABELS, type AaiDimension, type DepartmentAggregate } from '../../types/aai';
import { AggregateView, ErrorNote, Loading } from './components';
import { PageHeader, Banner } from './dashboards/ui';

// The team's weakest readiness dimension (lowest band midpoint) — the honest
// basis for the design's "Where to focus" nudge. Null when suppressed or unscored.
const lowestDimension = (agg: DepartmentAggregate): { dim: AaiDimension; pct: number } | null => {
  if (agg.suppressed || !agg.dimensions) return null;
  let best: { dim: AaiDimension; pct: number } | null = null;
  for (const dim of AAI_DIMENSIONS) {
    const r = agg.dimensions[dim];
    if (!r || typeof r.low !== 'number' || typeof r.high !== 'number') continue;
    const mid = (r.low + r.high) / 2;
    if (!best || mid < best.pct) best = { dim, pct: mid };
  }
  return best;
};

// "Team Rollup" (design §6.2) — the department in aggregate: stage distribution
// and confidence-weighted dimension bands only. No individual figures leave this
// view, and a group below the min size is suppressed (handled by AggregateView).
const TeamRollupPage: React.FC = () => {
  const { context, ready } = useParticipant();
  const department = context?.department ?? null;
  const [agg, setAgg] = useState<DepartmentAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!department) { setLoading(false); return; }
    (async () => {
      try {
        const res = await aaiApi.getDepartmentRollup(department);
        setAgg(res.data);
      } catch {
        setError('Could not load your team rollup.');
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, department]);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title="Team rollup"
        subtitle={`${department || 'Your department'}, in aggregate. Distributions and bands only — no individual figures leave this view.`}
      />
      <Banner tone="amber" icon={<Lock size={16} className="text-amber-600 shrink-0" />}>
        Aggregates only. You see how the team is distributed across stages — never any one person's readiness.
      </Banner>

      {loading ? (
        <Loading label="Loading your team rollup…" />
      ) : error ? (
        <ErrorNote message={error} />
      ) : !department ? (
        <p className="text-sm text-gray-500">No department is assigned to you yet.</p>
      ) : !agg ? (
        <p className="text-sm text-gray-500">No rollup yet — it appears once your team starts onboarding.</p>
      ) : (
        <>
          <AggregateView title={department} agg={agg} />
          {(() => {
            const focus = lowestDimension(agg);
            if (!focus) return null;
            return (
              <div className="mt-4 bg-black text-white rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2.5">
                  <Lightbulb size={16} className="text-accent-500" />
                  <span className="font-bold text-[14px]">Where to focus</span>
                </div>
                <p className="text-[13px] text-white/70 leading-relaxed m-0 mb-3.5">
                  {DIMENSION_LABELS[focus.dim]} ({Math.round(focus.pct * 100)}%) is your team's lowest dimension.
                  A deep-dive that turns a real team process into a skill would lift it fastest.
                </p>
                <Link to="/requests"
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-white text-black rounded-full px-4 py-2 hover:bg-gray-100 whitespace-nowrap">
                  Request a team deep-dive
                </Link>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default TeamRollupPage;
